import React, { memo } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import OutlinedInput from '@mui/material/OutlinedInput';
import Typography from '@mui/material/Typography';
import Apartment from '@mui/icons-material/ApartmentOutlined';
import Contacts from '@mui/icons-material/ContactsOutlined';
import Home from '@mui/icons-material/HomeOutlined';
import LocationOn from '@mui/icons-material/LocationOnOutlined';
import Public from '@mui/icons-material/Public';
import { alpha } from '@mui/material/styles';
import FormCard from './FormCard';
import FormField from './FormField';
import SegmentedControl, { Segment } from './SegmentedControl';
import ToggleChip from './ToggleChip';
import { AddressField, AddressType, DestinationAddress, isBlank } from './labelForm';
import { addButtonSx, inputSx } from './styles';

interface FieldDef {
  field: AddressField;
  label: string;
  /** Columns out of 6 on sm+; full width on phones. */
  span: number;
  required?: boolean;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
}

const FIELDS: readonly FieldDef[] = [
  { field: 'name', label: 'Recipient name', span: 3, required: true, inputProps: { autoComplete: 'name' } },
  { field: 'company', label: 'Company', span: 3, inputProps: { autoComplete: 'organization' } },
  { field: 'street1', label: 'Street address', span: 4, required: true, inputProps: { autoComplete: 'address-line1' } },
  { field: 'street2', label: 'Apt, suite', span: 2, inputProps: { autoComplete: 'address-line2' } },
  { field: 'city', label: 'City', span: 2, required: true, inputProps: { autoComplete: 'address-level2' } },
  { field: 'state', label: 'State', span: 1, required: true, inputProps: { autoComplete: 'address-level1' } },
  { field: 'postalCode', label: 'Postal code', span: 2, required: true, inputProps: { autoComplete: 'postal-code' } },
  { field: 'countryCode', label: 'Country', span: 1, required: true, inputProps: { maxLength: 2, autoComplete: 'country' } },
  { field: 'phoneNumber', label: 'Phone', span: 3, inputProps: { inputMode: 'tel', autoComplete: 'tel' } },
  { field: 'emailAddress', label: 'Email', span: 3, inputProps: { inputMode: 'email', type: 'email', autoComplete: 'email' } },
];

const ADDRESS_TYPES: ReadonlyArray<Segment<AddressType>> = [
  { value: 'residential', label: 'Residential', icon: <Home /> },
  { value: 'commercial', label: 'Commercial', icon: <Apartment /> },
];

interface ShipToCardProps {
  address: DestinationAddress;
  onFieldChange: (field: AddressField, value: string) => void;
  onAddressTypeChange: (type: AddressType) => void;
  customsIncluded: boolean;
  /** Customs forced on by the destination (international or APO/FPO/DPO). */
  customsRequired: boolean;
  international: boolean;
  onToggleCustoms: () => void;
  onOpenAddressBook: () => void;
}

const ShipToCard: React.FC<ShipToCardProps> = ({
  address,
  onFieldChange,
  onAddressTypeChange,
  customsIncluded,
  customsRequired,
  international,
  onToggleCustoms,
  onOpenAddressBook,
}) => (
  <FormCard
    icon={<LocationOn />}
    title="Ship to"
    actions={
      <Button variant="outlined" startIcon={<Contacts />} onClick={onOpenAddressBook} sx={addButtonSx}>
        Address book
      </Button>
    }
  >
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(6, minmax(0, 1fr))' }, gap: '14px 12px' }}>
      {FIELDS.map(({ field, label, span, required, inputProps }) => (
        <FormField key={field} label={label} required={required} optional={!required} sx={{ gridColumn: { xs: 'auto', sm: `span ${span}` } }}>
          <OutlinedInput
            value={address[field] ?? ''}
            onChange={(e) => onFieldChange(field, e.target.value)}
            error={required && isBlank(address[field])}
            inputProps={{ ...inputProps, 'aria-required': required }}
            sx={inputSx}
          />
        </FormField>
      ))}
    </Box>

    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', pt: 2, borderTop: 1, borderColor: 'divider' }}>
      <Typography component="span" sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary' }}>
        Address type
      </Typography>
      <SegmentedControl aria-label="Address type" value={address.addressType} options={ADDRESS_TYPES} onChange={onAddressTypeChange} />
      <Box sx={{ ml: { sm: 'auto' } }}>
        <ToggleChip label="Include customs info" selected={customsIncluded} disabled={customsRequired} onClick={onToggleCustoms} />
      </Box>
    </Box>

    {customsRequired && (
      <Box
        role="status"
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          px: 1.75,
          py: 1.25,
          borderRadius: 2.5,
          fontSize: 13,
          bgcolor: alpha(theme.palette.secondary.main, 0.1),
        })}
      >
        <Public sx={{ fontSize: 18, color: 'secondary.main' }} />
        {international
          ? `International shipment to ${address.countryCode} — customs information is required and turned on automatically.`
          : 'APO/FPO/DPO address — customs information is required and turned on automatically.'}
      </Box>
    )}
  </FormCard>
);

export default memo(ShipToCard);
